import type * as z from 'zod';

import type { ActorRole } from '../common/enums.js';
import type { ErrorCode } from '../common/errors.js';

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

/** Methods whose request travels as a JSON body; the rest travel as a query string. */
export const BODY_METHODS: readonly HttpMethod[] = ['POST', 'PUT', 'PATCH'];

export const API_CONTEXTS = [
  'auth',
  'admin',
  'methods',
  'method-graph',
  'validation',
  'catalog',
  'deliverables',
  'publishing',
  'workshop',
  'planning',
  'attachments',
  'ai-assist',
] as const;
export type ApiContext = (typeof API_CONTEXTS)[number];

/** `/api/methods/:methodVersionId/graph` → `'methodVersionId'`. */
export type PathParamNames<TPath extends string> = TPath extends `${string}:${infer Rest}`
  ? Rest extends `${infer Name}/${infer Tail}`
    ? Name | PathParamNames<Tail>
    : Rest
  : never;

export type PathParams<TPath extends string> = {
  readonly [K in PathParamNames<TPath>]: string;
};

/**
 * One endpoint. `request` describes the WHOLE input — path params, query and
 * body together — because a use case does not care which part of the transport
 * carried a value. `splitRequestInput` / `mergeRequestInput` translate between
 * that single schema and HTTP, and both ends of the wire use them.
 */
export interface RouteDefinition<
  TPath extends string = string,
  TRequest extends z.ZodType = z.ZodType,
  TResponse extends z.ZodType = z.ZodType,
> {
  readonly operationId: string;
  readonly method: HttpMethod;
  readonly path: TPath;
  readonly context: ApiContext;
  readonly summary: string;
  readonly request: TRequest;
  /** For a streaming route this is the shape of ONE chunk, not the whole body. */
  readonly response: TResponse;
  /** Empty means public. Otherwise the caller needs at least one of these roles. */
  readonly roles: readonly ActorRole[];
  readonly successStatus: number;
  /** NDJSON stream of `response` chunks instead of a single JSON body. */
  readonly streaming?: true;
  /** Error codes beyond the common set, documented in OpenAPI. */
  readonly errors?: readonly ErrorCode[];
}

/**
 * Identity helper that preserves the definition exactly as written: the literal
 * path (so `PathParams` still works), the concrete schema types, and whether
 * `streaming` was set at all — the client keys its return type on that, and
 * returning the interface type would widen it back to "maybe streaming".
 */
export const route = <const TDefinition extends RouteDefinition>(
  definition: TDefinition,
): TDefinition => definition;

export type AnyRoute = RouteDefinition;
